import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Avatar, Button, ErrorMessage, UploadIcon } from '@quickmd-llc/design-system'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'

const MAX_FILE_BYTES = 10 * 1024 * 1024
const ALLOWED_MIME = /(\/jpg|\/jpeg|\/png)$/i
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']

interface EditAvatarUploadProps {
  fullName: string
  profileImgInputValue: File | string | null | undefined
  isImageChanged: boolean
  setProfileImgInputValue: (value: File | string) => void
  setIsImageChanged: (value: boolean) => void
}

export function EditAvatarUpload({
  fullName,
  profileImgInputValue,
  isImageChanged,
  setProfileImgInputValue,
  setIsImageChanged,
}: EditAvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string>('')

  useEffect(
    () => () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl)
    },
    [localPreviewUrl],
  )

  const onPickFile = (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0]
    // Reset value so picking the same file again still fires onChange
    ev.target.value = ''
    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (file.size > MAX_FILE_BYTES) {
      setError(getUserFriendlyErrorMessage(errorCodes.TOO_BIG_FILE_SIZE, '', '10 MB'))
      return
    }
    if (!ALLOWED_MIME.test(file.type) || !ALLOWED_EXTENSIONS.includes(ext)) {
      setError(getUserFriendlyErrorMessage(errorCodes.INVALID_FILE_FORMAT, '', '.jpg, .jpeg, .png'))
      return
    }

    const previewUrl = URL.createObjectURL(file)
    setLocalPreviewUrl(previewUrl)
    setProfileImgInputValue(file)
    setIsImageChanged(true)
    setError('')
  }

  const avatarSrc =
    isImageChanged && localPreviewUrl ? localPreviewUrl
    : typeof profileImgInputValue === 'string' && profileImgInputValue ? profileImgInputValue
    : undefined

  const hasImage = Boolean(avatarSrc)

  return (
    <div className='flex shrink-0 flex-col items-center gap-sp-2'>
      {/* key forces remount when src changes so Avatar's internal imageError state resets */}
      {/*
       // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
      <Avatar key={avatarSrc ?? 'empty'} src={avatarSrc} name={fullName} size='xxl' />
      <Button type='button' priority='tertiary' size='s' onClick={() => inputRef.current?.click()}>
        <UploadIcon iconSize='s' />
        {hasImage ? 'Change photo' : 'Upload photo'}
      </Button>
      <input ref={inputRef} type='file' accept='image/png,image/jpeg' className='sr-only' onChange={onPickFile} />
      {error ?
        <ErrorMessage>{error}</ErrorMessage>
      : null}
    </div>
  )
}
